// Avantages (D-044, revus par D-100 ; maquettes 05). Ils s'ouvrent par un GRADE (la récompense de chaque grade,
// une fois par parcours), par une VICTOIRE (« après une victoire ») ou par une OFFRE de partenaire. Plus de solde
// de points ni de niveau. La page : « Pour toi maintenant », « Tes récompenses », « À débloquer », « Chez nos
// partenaires » (jamais pour une mineure, D-036, D-099 : la base les retire). Le membre touche « Utiliser », garde
// le doigt 5 secondes, et montre l'écran au commerçant (heure qui tourne, code, animation : une capture d'écran
// ne le trompe pas). Le bureau règle les avantages du club par la ⚙ de la page (D-139 : Réglages · Avantages,
// #/avantages/reglages ; #/avantages/gestion y mène) ; les récompenses des grades se règlent avec le bénévolat
// (D-127), un renvoi « ⚙ Bénévolat › » y mène.
// Lot 1 du pilote (D-103) : « Déjà reçues » (les récompenses utilisées, les avantages d'une seule fois déjà pris) ; une
// récompense qui m'attend n'est plus aussi « À débloquer » ; un partenaire n'a qu'une offre ; vouvoiement d'un partenaire.
// Nombre limité (D-131, maquettes a1 à a3) : « 1 fois par personne » (un compte, une utilisation) et « 5 en tout »,
// cumulables ; « Plus que 2 » pour les membres, « 3 partis sur 5 · plus que 2 » pour le bureau ; à 0, « Épuisé » (merci au
// partenaire) 7 jours dans « Pour toi maintenant », « Terminé » et « Remettre en jeu » pour le bureau.
// Validés par QR code (D-156) : « Utiliser » ouvre la fiche avec le QR (perk-qr.ts) ; l'appui de 5 s y reste en lien discret,
// noté « montré à l'écran » ; un parent montre celui de son enfant (« · pour Kenza », compté pour l'enfant) ; le bureau suit
// tout dans Réglages · Avantages › Validations (avec le prénom). Les récompenses des grades gardent l'appui de 5 s.
import './perks.css';
import {
  clubPerks, deletePerk, myPerkWins, partnersAdmin, perkStats, perksForMe, restockPerk, savePerk, useGradeReward,
  type Partner, type Perk, type PerkForMe, type PerkUse, type PerkWin,
} from '../lib/api';
import { FREQ_LABEL, PICTO_KEYS, PICTOS, pictoPath, type PerkFrequency } from '../domain/pictos';
import { endedLine, goneText, stockLine } from '../domain/perk-stock';
import { earnedRewards, gradeInfo, parcoursOf, toUnlock, unitOf, type GradeKey } from '../domain/badges';
import { parseLocalDate } from '../domain/schedule';
import { isMinorAccount, modOn, type Ctx } from './context';
import { DEFAULT_REWARDS } from '../domain/modules';
import { adoptGradeRewards, modulePreview } from '../lib/modules-api';
import { tv } from './vous';
import { clubMark } from './theme';
import { $, $$, confirmTwice, html, mount, openSheet, raw, toast, type Html } from './dom';
import { renvoi, settingsPage } from './nav';
import { gradeRewards, type RewardRow } from '../lib/reglages-api';
import { rewardsSummary } from '../domain/reglages';
import { openPerkQr, type HoldOpener, type QrPerkView } from './perk-qr';
import { perksForKids, perkValidations, type ClubValidation, type KidPerk } from '../lib/perk-qr-api';
import { bureauWhere, HOW_LABEL } from '../domain/perk-qr';
import { time } from './format';

const HOLD_MS = 5000;
const isAdmin = (ctx: Ctx) => !!ctx.club && ctx.adminOf.some(c => c.id === ctx.club!.id);
const lowerFirst = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);

/** Un picto de la bibliothèque (tracés constants de l'app : sûrs à insérer). */
export const picto = (key: string | null | undefined, size = 24): Html =>
  raw(`<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="${pictoPath(key)}"/></svg>`);

function blockText(p: PerkForMe): string {
  switch (p.block) {
    case 'used_today': return 'Déjà utilisé aujourd’hui';
    case 'used_month': return 'Déjà utilisé ce mois-ci';
    case 'used_once': return 'Déjà utilisé';
    case 'no_match_today': return 'Les jours de match seulement';
    case 'no_win': return `Après une victoire${p.team_name ? ` de ${p.team_name}` : ''}`;
    case 'exhausted': return goneText(p);
    default: return 'Indisponible';
  }
}

/** « Plus que 2 » (membres) : un avantage limité encore disponible. */
const leftChip = (p: PerkForMe): Html | '' => (p.stock != null && p.stock_left != null && !p.block ? html`<span class="chip chip-accent">Plus que ${p.stock_left}</span>` : '');

/** « dimanche », ou « le 12 octobre » au-delà d'une semaine. */
const dayWord = (d: string) => {
  const t = parseLocalDate(d.slice(0, 10)), days = (Date.now() - t.getTime()) / 86_400_000;
  return Math.abs(days) < 6 ? t.toLocaleDateString('fr-FR', { weekday: 'long' }) : `le ${t.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}`;
};
/** « 12 octobre ». */
const longDate = (iso: string) => new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
/** « SF1 a gagné dimanche : jusqu'à mercredi soir. » */
const winText = (w: PerkWin) => `${w.team_name} a gagné ${dayWord(w.won_at)} : jusqu’${dayWord(w.until).startsWith('le ') ? `au ${dayWord(w.until).slice(3)}` : `à ${dayWord(w.until)}`} soir.`;

interface Card { picto: string; title: string; details?: string | null; chips?: Html[]; use?: string; off?: boolean; why?: string; bar?: number; guide?: boolean }
const chipMute = (t: string) => html`<span class="chip chip-mute">${t}</span>`;
const gradeChip = (g: GradeKey) => html`<span class="gr-chip g-${g}">${gradeInfo(g).name}</span>`;
const card = (c: Card) => html`
  <li class="pk ${c.off ? 'off' : ''}">
    <span class="pk-ico">${picto(c.picto, 24)}</span>
    <span class="pk-main"><strong>${c.title}</strong>${c.details ? html`<span class="muted small">${c.details}</span>` : ''}
      ${c.chips?.length ? html`<span class="pk-chips">${c.chips}</span>` : ''}
      ${c.bar != null ? html`<span class="gr-pkbar" role="img" aria-label="${c.bar} %"><span style="width:${c.bar}%"></span></span>` : ''}
      ${c.why ? html`<span class="pk-why">${c.why}</span>` : ''}</span>
    ${c.use ? html`<button type="button" class="btn btn-primary btn-sm" ${c.guide ? html`data-guide="perk-use"` : ''} data-use="${c.use}">Utiliser</button>` : ''}
  </li>`;

export async function renderPerks(root: HTMLElement, ctx: Ctx): Promise<void> {
  const club = ctx.club;
  if (!club) { mount(root, html`<div class="empty"><p class="empty-title">Aucun club.</p></div>`); return; }
  const { loadBadges, rewardsToUse } = await import('./badges');
  const [list, wins, d, kids] = await Promise.all([
    perksForMe(club.id).catch(() => [] as PerkForMe[]), myPerkWins(club.id).catch(() => [] as PerkWin[]), loadBadges(ctx).catch(() => null),
    ctx.people.some(x => x.user_id !== ctx.uid) ? perksForKids(club.id).catch(() => [] as KidPerk[]) : Promise.resolve([] as KidPerk[]),
  ]);
  // Les avantages d'avant D-100 réservés à un niveau ou payés en points sont en pause (la base les dit « inactive »).
  const shown = list.filter(p => p.block !== 'inactive' && !p.min_level && !p.cost_points);
  const own = shown.filter(p => !p.partner_name);
  const partners = shown.filter(p => p.partner_name);
  // « pour Kenza » : seulement pour un enfant, jamais pour soi (plus de « · Sarah » orphelin).
  const forOther = (p: PerkForMe) => !!p.person_id && ctx.people.find(x => x.id === p.person_id)?.user_id !== ctx.uid;
  const now = own.filter(p => !p.block);
  const later = own.filter(p => p.block && p.frequency !== 'per_win' && p.block !== 'used_once' && p.block !== 'exhausted');
  // Épuisés (D-131) : 7 jours dans « Pour toi maintenant », avec un merci au partenaire ; une fois par avantage.
  const gone = [...new Map(shown.filter(p => p.block === 'exhausted').map(p => [p.id, p])).values()];
  const winLocked = [...new Map(own.filter(p => p.frequency === 'per_win' && p.block === 'no_win').map(p => [p.id, p])).values()]
    .filter(p => !now.some(n => n.id === p.id));
  const rewards = d ? rewardsToUse(d) : [];
  // Une récompense qui m'attend (« Tes récompenses ») n'est pas aussi « À débloquer » (D-103).
  const unlock = d ? toUnlock(d.states, d.b.rewards, new Set(rewards.map(r => r.reward.grade))) : [];
  // Déjà reçues : les récompenses des grades utilisées, les avantages d'une seule fois déjà pris.
  const received = d ? earnedRewards(d.states, d.b.rewards, d.b.uses).filter(r => r.used_at)
    .sort((a, b) => (b.used_at ?? '').localeCompare(a.used_at ?? '')) : [];
  const usedOnce = shown.filter(p => p.block === 'used_once');
  const t = (tu: string, vous: string) => tv(ctx, tu, vous);
  const actions = new Map<string, () => void>();
  const reload = () => renderPerks(root, ctx);
  let first = true;
  const useKey = (k: string, fn: () => void) => { actions.set(k, fn); const g = first; first = false; return { use: k, guide: g }; };
  // D-156 : « Utiliser » ouvre la fiche avec le QR ; l'appui de 5 s s'ouvre de la fiche (« ‹ Revenir au QR »).
  const hold = (v: QrPerkView): HoldOpener => (run, back, onDone) => openUseHold(ctx,
    { title: v.title, picto: v.picto, details: v.details, partner_name: v.partner_name, person_name: v.for_name }, run, onDone, { back });
  const qr = (v: QrPerkView) => () => openPerkQr(ctx, v, hold(v), () => void reload());
  const view = (p: PerkForMe, personId: string | null, forName: string | null): QrPerkView => ({
    perkId: p.id, personId, title: p.title, picto: p.picto, details: p.details, frequency: p.frequency, partner_name: p.partner_name,
    partner_logo: p.partner_logo, stock_left: p.stock_left, for_name: forName });

  const nowCards = now.map(p => {
    const w = p.frequency === 'per_win' ? wins.find(x => x.perk_id === p.id && x.person_id === p.person_id) : undefined;
    return card({ picto: p.picto, title: `${p.title}${forOther(p) ? ` · pour ${p.person_name}` : ''}`, details: w ? winText(w) : p.details,
      chips: [chipMute(FREQ_LABEL[p.frequency]), leftChip(p)].filter((c): c is Html => !!c),
      ...useKey(`p:${p.id}:${p.person_id ?? ''}`, qr(view(p, p.person_id, forOther(p) ? p.person_name : null))) });
  });
  // Un parent montre le QR de son enfant (D-156) : ce que l'enfant peut utiliser maintenant, « · pour Kenza ».
  const kidCards = kids.filter(k => !k.block).map(k => card({ picto: k.picto, title: `${k.partner_name ? `${k.partner_name} · ${lowerFirst(k.title)}` : k.title} · pour ${k.person_name}`,
    details: k.details, chips: [chipMute(FREQ_LABEL[k.frequency]), k.stock_left != null ? html`<span class="chip chip-accent">Plus que ${k.stock_left}</span>` : ''].filter((c): c is Html => !!c),
    ...useKey(`k:${k.id}:${k.person_id}`, qr({ perkId: k.id, personId: k.person_id, title: k.title, picto: k.picto, details: k.details, frequency: k.frequency,
      partner_name: k.partner_name, partner_logo: null, stock_left: k.stock_left, for_name: k.person_name })) }));
  const rewardCards = rewards.map(r => {
    const why = `Pour ${t('ton', 'votre')} grade ${gradeInfo(r.reward.grade).name} en ${r.parcours.name}.`;
    return card({ picto: r.reward.picto, title: r.reward.title, details: why, chips: [gradeChip(r.reward.grade), chipMute('1 fois par parcours')],
      ...useKey(`r:${r.reward.grade}:${r.parcours.key}`, () => openUseHold(ctx, { title: r.reward.title, picto: r.reward.picto, details: why },
        () => useGradeReward(club.id, r.reward.grade, r.parcours.key), () => void reload())) });
  });
  const unlockCards = [
    ...unlock.map(u => {
      const where = u.reward.parcours?.length ? `dans ${u.reward.parcours.map(x => parcoursOf(x).name).join(', ')}` : 'dans n’importe quel parcours';
      const assid = u.parcours?.key === 'assiduite';
      const unit = assid ? (u.toGo > 1 ? 'semaines complètes' : 'semaine complète') : unitOf('', u.toGo);
      const again = received.some(r => r.reward.grade === u.reward.grade);   // déjà reçue dans un autre parcours : une fois par parcours
      return card({ picto: u.reward.picto, title: u.reward.title, details: `${again ? 'Encore une fois : ' : ''}${again ? 'au' : 'Au'} grade ${gradeInfo(u.reward.grade).name}, ${where}.`, chips: [gradeChip(u.reward.grade)], off: true,
        bar: u.parcours ? u.pct : undefined,
        why: u.parcours ? `${assid ? 'Assiduité :' : `Le plus proche : ${u.parcours.name},`} encore ${u.toGo} ${unit}` : `${u.toGo} missions dans un même parcours` });
    }),
    ...winLocked.map(p => card({ picto: p.picto, title: p.title, details: p.details, chips: [chipMute(FREQ_LABEL.per_win)], off: true, why: blockText(p) })),
  ];
  const goneCards = gone.map(p => card({ picto: p.picto, title: p.title, details: p.partner_name ? `Offert par ${p.partner_name}` : p.details,
    chips: [chipMute('Épuisé')], off: true, why: goneText(p) }));
  const laterCards = later.map(p => card({ picto: p.picto, title: p.title, details: p.details, chips: [chipMute(FREQ_LABEL[p.frequency])], off: true, why: blockText(p) }));
  const receivedCards = [
    ...received.map(r => card({ picto: r.reward.picto, title: r.reward.title, details: `Grade ${gradeInfo(r.reward.grade).name} en ${r.parcours.name}.`,
      chips: [gradeChip(r.reward.grade)], off: true, why: `Reçue le ${longDate(r.used_at!)}` })),
    ...usedOnce.map(p => card({ picto: p.picto, title: p.partner_name ? `${p.partner_name} · ${lowerFirst(p.title)}` : `${p.title}${forOther(p) ? ` · pour ${p.person_name}` : ''}`,
      details: p.details, chips: [chipMute(FREQ_LABEL.once)], off: true, why: 'Déjà utilisé' })),
  ];
  const partnerCards = partners.filter(p => p.block !== 'used_once' && p.block !== 'exhausted').map(p => card({ picto: p.picto, title: `${p.partner_name} · ${lowerFirst(p.title)}`, details: p.details,
    chips: [chipMute(FREQ_LABEL[p.frequency]), leftChip(p)].filter((c): c is Html => !!c), off: !!p.block, why: p.block ? blockText(p) : undefined,
    ...(p.block ? {} : useKey(`p:${p.id}:`, qr(view(p, null, null)))) }));
  const sec = (title: string, cards: Html[], id = '') => cards.length ? html`
    <section class="block" ${id ? html`id="${id}"` : ''}><div class="block-head"><h3>${title}</h3></div><ul class="pk-list">${cards}</ul></section>` : '';

  mount(root, html`
    <section class="screen">
      ${isAdmin(ctx) ? html`<a class="back" href="#/club">‹ Le club</a>` : html`<a class="back" href="#/moi">‹ Moi</a>`}
      <header class="screen-head"><p class="eyebrow">${club.name}</p><h2>Avantages</h2>
        <p class="muted">${t('Touche « Utiliser » au moment de payer : le commerçant scanne ton QR avec son téléphone.', 'Touchez « Utiliser » au moment de payer : le commerçant scanne votre QR avec son téléphone.')}</p></header>
      ${sec(t('Pour toi maintenant', 'Pour vous maintenant'), [...nowCards, ...kidCards, ...goneCards])}
      ${sec(t('Tes récompenses', 'Vos récompenses'), rewardCards)}
      ${sec('À débloquer', unlockCards, 'gr-alock')}
      ${sec('Plus tard', laterCards)}
      ${sec('Chez nos partenaires', partnerCards)}
      ${sec('Déjà reçues', receivedCards, 'pk-recues')}
      ${nowCards.length + kidCards.length + goneCards.length + rewardCards.length + unlockCards.length + laterCards.length + partnerCards.length + receivedCards.length ? ''
        : html`<div class="empty"><p class="empty-title">Pas encore d'avantage</p><p>${isMinorAccount(ctx) ? 'Le club les proposera ici : récompense d’un grade, glace après une victoire…'
            : 'Le club et ses partenaires les proposeront ici : récompense d’un grade, glace après une victoire, offre d’un commerçant…'}</p></div>`}
    </section>`);

  for (const b of $$<HTMLButtonElement>(root, '[data-use]')) b.addEventListener('click', () => actions.get(b.dataset.use!)?.());
}

/** Ce que montre l'écran d'un avantage (ou d'une récompense de grade). */
export interface UseView { title: string; picto: string; details?: string | null; partner_name?: string | null; person_name?: string | null }

/** Utiliser : le membre garde le doigt 5 secondes, puis montre l'écran au commerçant (`run` enregistre et rend le code).
 *  Depuis la fiche du QR (D-156, maquette a3) : « ‹ Revenir au QR » (`back`) et la note « montré à l'écran ». */
export function openUseHold(ctx: Ctx, p: UseView, run: () => Promise<PerkUse>, onDone: () => void, opts: { back?: () => void } = {}): void {
  const host = document.createElement('div');
  host.className = 'pk-over';
  host.setAttribute('role', 'dialog');
  host.setAttribute('aria-modal', 'true');
  host.setAttribute('aria-label', `Utiliser : ${p.title}`);
  document.body.append(host);
  let timer = 0, t0 = 0, busy = false, clock = 0;
  // Fermer sans avoir validé, depuis la fiche du QR : retour au QR (« ‹ Revenir au QR », Échap).
  const close = (done = false) => { cancelAnimationFrame(timer); clearInterval(clock); host.remove(); document.removeEventListener('keydown', onEsc); if (done) onDone(); else opts.back?.(); };
  const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) close(); };
  document.addEventListener('keydown', onEsc);
  const R = 54, C = 2 * Math.PI * R;

  mount(host, html`
    <div class="pk-stage">
      <button type="button" class="pk-x" aria-label="Fermer">×</button>
      ${opts.back ? html`<button type="button" class="qx-back" id="qx-back">‹ Revenir au QR</button>` : ''}
      <span class="pk-big-ico">${picto(p.picto, 44)}</span>
      <p class="eyebrow">${p.partner_name ?? ctx.club?.name ?? ''}</p>
      <h2 class="pk-title">${p.title}</h2>
      ${p.person_name ? html`<p class="muted">pour ${p.person_name}</p>` : ''}
      ${p.details ? html`<p class="small">${p.details}</p>` : ''}
      <button type="button" class="pk-hold" id="pk-hold" aria-label="Appuie 5 secondes pour utiliser l'avantage">
        <svg viewBox="0 0 128 128" aria-hidden="true"><circle class="pk-ring-bg" cx="64" cy="64" r="${R}"/>
          <circle class="pk-ring" id="pk-ring" cx="64" cy="64" r="${R}" stroke-dasharray="${C}" stroke-dashoffset="${C}"/></svg>
        <span id="pk-hold-txt">${tv(ctx, 'Appuie', 'Appuyez')}<br>5 secondes</span>
      </button>
      <p class="hint" id="pk-hint">${tv(ctx, 'Garde le doigt appuyé 5 secondes devant le commerçant, puis montre-lui l’écran. Relâche pour annuler.',
        'Gardez le doigt appuyé 5 secondes devant le commerçant, puis montrez-lui l’écran. Relâchez pour annuler.')}</p>
      ${opts.back ? html`<p class="qx-holdnote"><b>Sans scan, personne ne valide rien :</b> ${p.partner_name ? 'le commerçant regarde' : 'on regarde'} l’écran vert, le code et l’heure qui tourne. L’avantage est compté quand même, noté « montré à l’écran ».</p>` : ''}
    </div>`);

  const btn = $<HTMLButtonElement>(host, '#pk-hold')!;
  const ring = $(host, '#pk-ring')!;
  const txt = $(host, '#pk-hold-txt')!;
  // × ferme tout (la fiche du QR aussi) ; « ‹ Revenir au QR » ramène au QR.
  $(host, '.pk-x')!.addEventListener('click', () => { if (busy) return; const b = opts.back; opts.back = undefined; close(); if (b) onDone(); });
  $(host, '#qx-back')?.addEventListener('click', () => { if (!busy) close(); });
  const reset = () => {
    cancelAnimationFrame(timer); t0 = 0;
    ring.setAttribute('stroke-dashoffset', String(C));
    if (!busy) txt.innerHTML = `${tv(ctx, 'Appuie', 'Appuyez')}<br>5 secondes`;
  };
  const tick = () => {
    const k = Math.min(1, (performance.now() - t0) / HOLD_MS);
    ring.setAttribute('stroke-dashoffset', String(C * (1 - k)));
    txt.textContent = `${Math.ceil((HOLD_MS - (performance.now() - t0)) / 1000)}`;
    if (k >= 1) { void confirm(); return; }
    timer = requestAnimationFrame(tick);
  };
  const start = (e: Event) => {
    if (busy || t0) return;
    e.preventDefault();
    t0 = performance.now();
    try { navigator.vibrate?.(30); } catch { /* sans vibreur */ }
    timer = requestAnimationFrame(tick);
  };
  const stop = () => { if (!busy && t0) reset(); };
  btn.addEventListener('pointerdown', start);
  for (const ev of ['pointerup', 'pointerleave', 'pointercancel']) btn.addEventListener(ev, stop);
  btn.addEventListener('contextmenu', e => e.preventDefault());
  btn.addEventListener('keydown', e => { if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) start(e); });
  btn.addEventListener('keyup', e => { if (e.key === ' ' || e.key === 'Enter') stop(); });

  const confirm = async () => {
    busy = true;
    cancelAnimationFrame(timer);
    txt.textContent = '…';
    try {
      const u = await run();
      try { navigator.vibrate?.([60, 40, 120]); } catch { /* sans vibreur */ }
      const fmt = () => new Date().toLocaleString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit', second: '2-digit' });
      host.classList.add('done');
      mount(host, html`
        <div class="pk-ok">
          <span class="pk-pulse" aria-hidden="true"></span>
          <svg class="pk-check" viewBox="0 0 52 52" aria-hidden="true"><circle cx="26" cy="26" r="24"/><path d="M14 27l8 8 16-17"/></svg>
          <p class="pk-ok-eyebrow">Avantage validé</p>
          <h2 class="pk-ok-title">${u.title}</h2>
          <p class="pk-ok-who">${u.partner ? `${u.partner} · ` : ''}pour ${u.who}</p>
          <p class="pk-ok-code" aria-label="Code ${u.code.split('').join(' ')}">${u.code}</p>
          <p class="pk-ok-time" id="pk-clock" aria-live="off">${fmt()}</p>
          <span class="pk-ok-club">${clubMark(ctx.club ?? null, 'sm')}<span>${ctx.club?.name ?? ''}</span></span>
          <button type="button" class="btn pk-ok-btn" id="pk-done">Terminé</button>
        </div>`);
      clock = window.setInterval(() => { const c = $(host, '#pk-clock'); if (c) c.textContent = fmt(); }, 1000);
      $(host, '#pk-done')!.addEventListener('click', () => close(true));
    } catch (e) {
      busy = false;
      const m = String((e as Error).message);
      toast(m.includes('indisponible') ? 'Cet avantage n’est plus disponible.' : tv(ctx, 'Validation impossible. Réessaie.', 'Validation impossible. Réessayez.'), 'ko');
      close(true);
    }
  };
}

// -----------------------------------------------------------------------------
// Bureau : Réglages · Avantages (D-139, maquette n1) — les avantages du club et des partenaires (chacun s'ouvre en
// feuille), les récompenses des grades en renvoi vers les réglages du bénévolat (D-127).
// -----------------------------------------------------------------------------
export async function renderPerksAdmin(root: HTMLElement, ctx: Ctx, open?: string): Promise<void> {
  const club = ctx.club;
  if (!club || !isAdmin(ctx)) return renderPerks(root, ctx);
  const [perks, partners, stats, pv, rewards, vals] = await Promise.all([
    clubPerks(club.id).catch(() => [] as Perk[]), partnersAdmin(club.id).catch(() => [] as Partner[]), perkStats(club.id).catch(() => []),
    modOn(ctx, 'benevolat') ? modulePreview(club.id, 'avantages').catch(() => null) : Promise.resolve(null),
    modOn(ctx, 'benevolat') ? gradeRewards(club.id).catch(() => [] as RewardRow[]) : Promise.resolve([] as RewardRow[]),
    perkValidations(club.id).catch(() => [] as ClubValidation[]),
  ]);
  // D-122 : les récompenses des grades sont proposées (jamais créées d'office) ; le bureau les reprend en un geste.
  const proposed = DEFAULT_REWARDS.filter(r => pv?.rewards_missing?.includes(r.grade));
  const st = new Map(stats.map(s => [s.perk_id, s]));
  const nameOf = new Map(partners.map(p => [p.id, p.name]));
  // Une seule offre par partenaire (D-103) : la sienne (en service, sinon la dernière) ; les anciennes, en pause, ne s'affichent plus.
  const offerOf = new Map<string, Perk>();
  for (const k of perks) if (k.partner_id) { const o = offerOf.get(k.partner_id); if (!o || k.active || !o.active) offerOf.set(k.partner_id, k); }
  const listed = perks.filter(k => !k.partner_id || offerOf.get(k.partner_id)?.id === k.id);
  const legacy = (p: Perk) => !!p.min_level || p.cost_points > 0;
  const reload = () => renderPerksAdmin(root, ctx);
  const ended = listed.filter(p => p.stock != null && !!p.exhausted_at).length;
  const perksBody = (el: HTMLElement) => {
    mount(el, html`
      <p class="muted small nv-intro">Du club ou d’un partenaire, limités, ou après une victoire. Les offres validées des partenaires arrivent ici toutes seules.</p>
      ${listed.length ? html`<ul class="pk-list" data-guide="pk-list">${listed.map(p => {
        const s = st.get(p.id);
        // Nombre limité (maquette a2) : la barre et « 3 partis sur 5 · plus que 2 » ; épuisé : « Terminé », « Remettre en jeu ».
        const out = p.stock != null && !!p.exhausted_at;
        const pct = p.stock ? Math.min(100, Math.round(p.stock_used / p.stock * 100)) : 0;
        return html`<li class="pk ${p.active && !out ? '' : 'off'}"><span class="pk-ico">${picto(p.picto, 26)}</span>
          <span class="pk-body"><button type="button" class="pk-main pk-edit" data-edit="${p.id}">
            <strong>${p.title}</strong>
            <span class="small">${p.partner_id ? `Offre de ${nameOf.get(p.partner_id) ?? 'Partenaire'}` : 'Le club'} · ${FREQ_LABEL[p.frequency]}${p.stock != null ? ` · ${p.stock} en tout` : ''}${p.active ? '' : ' · en pause'}</span>
            ${legacy(p) ? html`<span class="pk-why">Réservé à un niveau ou payé en points avant les badges : la récompense d’un grade le remplace. Rouvre-le pour le proposer à tous.</span>` : ''}
            ${out ? '' : p.stock != null ? html`<span class="pk-stock"><span class="pk-bar" role="img" aria-label="${p.stock_used} sur ${p.stock}"><i style="width:${pct}%"></i></span>
              <span class="muted small">${stockLine(p.stock_used, p.stock)}</span></span>`
              : html`<span class="muted small">Utilisé : ${s?.today ?? 0} aujourd'hui · ${s?.month ?? 0} ce mois · ${s?.total ?? 0} en tout</span>`}
          </button>
          ${out ? html`<span class="pk-why">${endedLine(p.stock!, p.exhausted_at!)} <button type="button" class="pk-again" data-again="${p.id}" data-guide="pk-again">Remettre en jeu</button></span>` : ''}
          </span></li>`;
      })}</ul>` : html`<div class="empty"><p class="empty-title">Aucun avantage</p><p>Ex. « café offert les jours de match », « glace après une victoire ».</p></div>`}
      <button type="button" class="btn btn-primary btn-sm" id="pk-new" data-guide="pk-new">+ Avantage</button>`);
    $(el, '#pk-new')!.addEventListener('click', () => openPerkEdit(ctx, null, partners, reload));
    for (const b of $$<HTMLButtonElement>(el, '[data-edit]')) b.addEventListener('click', () => openPerkEdit(ctx, perks.find(p => p.id === b.dataset.edit)!, partners, reload));
    for (const b of $$<HTMLButtonElement>(el, '[data-again]')) b.addEventListener('click', () => openRestock(perks.find(p => p.id === b.dataset.again)!, nameOf, reload));
  };
  // Les récompenses des grades (D-127) : réglées avec le bénévolat ; ici, celles que le club n'a pas encore reprises.
  const rewardsBody = (el: HTMLElement) => {
    mount(el, html`
      ${proposed.length ? html`<section class="card pk-propose" data-guide="pk-rewards">
        <span class="card-main"><strong>Récompenses des grades proposées</strong>
          <span class="muted small">Une fois par parcours, pour les bénévoles qui montent en grade : ${proposed.map(r => `${r.label} : ${r.title.charAt(0).toLowerCase()}${r.title.slice(1)}`).join(' · ')}. Rien n’est promis tant que tu ne les reprends pas.</span>
          <span class="btn-row"><button type="button" class="btn btn-primary btn-sm" id="pk-adopt">Les reprendre</button></span></span></section>` : ''}
      ${renvoi('Elles se règlent avec les parcours des bénévoles', 'Bénévolat', '#/benevoles/reglages/recompenses', 'pk-rewards-go')}`);
    $(el, '#pk-adopt')?.addEventListener('click', async (e) => {
      const b = e.currentTarget as HTMLButtonElement;
      b.disabled = true;
      try { await adoptGradeRewards(club.id); toast('Les récompenses des grades sont reprises'); void reload(); }
      catch { b.disabled = false; toast('Impossible pour le moment.', 'ko'); }
    });
  };
  // Validations (D-156, maquette d2) : tout le club, avec le prénom, l'avantage, qui l'offre, qui a validé et comment.
  const monthKey = new Date().toISOString().slice(0, 7);
  const ofMonth = vals.filter(v => new Date(v.used_at).toISOString().slice(0, 7) === monthKey);
  const validationsBody = (el: HTMLElement) => {
    let filter: 'all' | 'partners' | 'club' = 'all';
    const draw = () => {
      const rows = vals.filter(v => filter === 'all' || (filter === 'partners') === !!v.partner_name);
      const month = rows.filter(v => new Date(v.used_at).toISOString().slice(0, 7) === monthKey);
      const n = (m: ClubValidation['method']) => month.filter(v => v.method === m).length;
      const groups: { label: string; rows: ClubValidation[] }[] = [];
      for (const v of rows.slice(0, 60)) {
        const label = dayLabel(v.used_at);
        const g = groups[groups.length - 1];
        if (g && g.label === label) g.rows.push(v); else groups.push({ label, rows: [v] });
      }
      mount(el, html`
        <div class="chip-toggles" role="group" aria-label="Filtrer les validations">${([['all', 'Tous'], ['partners', 'Partenaires'], ['club', 'Club']] as const).map(([k, l]) => html`
          <button type="button" class="chip-toggle" data-f="${k}" aria-pressed="${String(filter === k)}">${l}</button>`)}</div>
        <div class="lic-kpis qx-kp"><span><b>${month.length}</b><small>ce mois</small></span><span><b>${n('qr')}</b><small>QR</small></span>
          <span><b>${n('code')}</b><small>code</small></span><span><b>${n('hold')}</b><small>à l’écran</small></span></div>
        ${groups.length ? groups.map(g => html`<p class="qx-day">${g.label}</p><ul class="qx-vl">${g.rows.map(v => html`
          <li><span class="pk-ico">${picto(v.picto, 18)}</span><span><strong>${v.member} · ${v.title}</strong><small>${bureauWhere(v)}</small>
            <small>${howChip(v.method)}</small></span><time>${time(new Date(v.used_at))}</time></li>`)}</ul>`)
          : html`<p class="muted small">Aucune validation pour l’instant : chaque avantage utilisé (QR scanné, code tapé, montré à l’écran) s’inscrit ici.</p>`}
        <p class="hint">Le partenaire voit ses validations sans les noms ; le bureau voit tout. « Montré à l’écran » : l’appui de 5 s, sans validation du commerçant.</p>`);
      for (const b of $$<HTMLButtonElement>(el, '[data-f]')) b.addEventListener('click', () => { filter = b.dataset.f as typeof filter; draw(); });
    };
    draw();
  };
  settingsPage(root, {
    key: 'avantages', open: open ?? null,
    intro: 'Les avantages du club et les offres des partenaires : les créer, les limiter, les remettre en jeu.',
    rubrics: [
      { key: 'avantages', icon: 'ti-gift', title: 'Avantages du club et des partenaires', guide: 'pk-sec',
        sum: listed.length ? `${listed.length}${ended ? ` · dont ${ended} ${ended > 1 ? 'terminés' : 'terminé'}` : ''}` : 'Aucun pour l’instant', body: perksBody },
      { key: 'validations', icon: 'ti-qrcode', title: 'Validations', guide: 'pk-valid-sec',
        sum: ofMonth.length ? `${ofMonth.length} ce mois · ${ofMonth.filter(v => v.method === 'qr').length} par QR` : 'Aucune ce mois-ci', body: validationsBody },
      ...(modOn(ctx, 'benevolat') ? [{ key: 'recompenses', icon: 'ti-star', title: 'Récompenses des grades', guide: 'pk-rewards-sec',
        sum: proposed.length ? 'Proposées : à reprendre' : rewardsSummary(rewards), body: rewardsBody }] : []),
    ],
    openByDefault: ['avantages', 'recompenses'],
  }, reload);
}

/** La façon dont un avantage a été validé (D-156) : « QR scanné », « Code tapé », « Montré à l’écran ». */
export const howChip = (m: ClubValidation['method']): Html => html`<span class="qx-how ${m}">${HOW_LABEL[m]}</span>`;
/** « Aujourd’hui », « Hier », « jeu. 29 oct. ». */
export function dayLabel(iso: string): string {
  const d = new Date(iso), t = new Date();
  const k = (x: Date) => x.toDateString();
  if (k(d) === k(t)) return 'Aujourd’hui';
  t.setDate(t.getDate() - 1);
  if (k(d) === k(t)) return 'Hier';
  return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
}

/** « Remettre en jeu » : un nouveau nombre (le dernier proposé), une nouvelle série ; « 1 fois par personne » reste acquis. */
function openRestock(p: Perk, nameOf: Map<string, string>, onDone: () => void): void {
  const who = p.partner_id ? nameOf.get(p.partner_id) ?? null : null;
  openSheet('Remettre en jeu', html`
    <form class="form" id="f-again" novalidate>
      <p class="small"><strong>${p.title}</strong>${who ? ` · offre de ${who}` : ''}</p>
      ${p.exhausted_at && p.stock ? html`<p class="muted small">${endedLine(p.stock, p.exhausted_at)}</p>` : ''}
      <div class="row-2"><label class="field"><span>Combien cette fois</span><input type="number" name="stock" inputmode="numeric" min="1" max="10000" value="${p.stock ?? 5}" required></label><span></span></div>
      <p class="hint">Premiers arrivés, premiers servis, comme la première fois.${p.frequency === 'once' ? ' Ceux qui l’ont déjà eu ne le reprennent pas (1 fois par personne).' : ''}${who ? ` Avec l’accord de ${who}.` : ''}</p>
      <p class="form-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit">Remettre en jeu</button>
    </form>`, (sheet, close) => {
    const f = $<HTMLFormElement>(sheet, '#f-again')!;
    f.addEventListener('submit', async (e) => {
      e.preventDefault();
      const n = Math.floor(Number(new FormData(f).get('stock')));
      if (!(n >= 1 && n <= 10000)) { $(f, '.form-msg')!.textContent = 'Combien en tout : au moins 1.'; return; }
      try { await restockPerk(p.id, n); close(); toast(`Remis en jeu : ${n} en tout`); onDone(); }
      catch { $(f, '.form-msg')!.textContent = 'Impossible pour le moment.'; }
    });
  });
}

function openPerkEdit(ctx: Ctx, p: Perk | null, partners: Partner[], onDone: () => void) {
  const club = ctx.club!;
  const teams = ctx.teams.filter(t => t.club_id === club.id);
  let pic = p?.picto ?? 'cadeau';
  openSheet(p ? 'Modifier l’avantage' : 'Nouvel avantage', html`
    <form class="form" id="f-pk" novalidate>
      <label class="field"><span>Avantage</span><input name="title" maxlength="80" required value="${p?.title ?? ''}" placeholder="ex. Café offert, -10 % sur la révision"></label>
      <label class="field"><span>Détails (facultatif)</span><input name="details" maxlength="500" value="${p?.details ?? ''}" placeholder="ex. Sur présentation de l’app, au comptoir"></label>
      <label class="field"><span>Offert par</span><select name="partner"><option value="">Le club</option>
        ${partners.map(x => html`<option value="${x.id}" ${p?.partner_id === x.id ? 'selected' : ''}>${x.name}</option>`)}</select></label>
      <p class="hint pk-offer-hint" ${p?.partner_id ? '' : 'hidden'}>Un partenaire n’a qu’une offre : c’est elle, celle de « Nos partenaires ». Enregistrée, elle remplace son offre en ligne ; en pause, elle n’est plus affichée.</p>
      <div class="field"><span>Picto</span><div class="pk-pictos" role="radiogroup" aria-label="Picto">${PICTO_KEYS.map(k => html`
        <button type="button" role="radio" class="pk-pic" data-pic="${k}" aria-pressed="${String(k === pic)}" aria-label="${PICTOS[k]!.label}" title="${PICTOS[k]!.label}">${picto(k, 22)}</button>`)}</div></div>
      <div class="row-2">
        <label class="field"><span>Combien de fois</span><select name="frequency">${(Object.keys(FREQ_LABEL) as PerkFrequency[]).map(f => html`
          <option value="${f}" ${(p?.frequency ?? 'unlimited') === f ? 'selected' : ''}>${FREQ_LABEL[f]}</option>`)}</select></label>
        <label class="field"><span>Jusqu'au (facultatif)</span><input name="until" type="date" value="${p?.valid_until ?? ''}"></label>
      </div>
      <div class="row-2 pk-win" ${p?.frequency === 'per_win' ? '' : 'hidden'}>
        <label class="field"><span>Équipe</span><select name="team"><option value="">Toutes les équipes</option>
          ${teams.map(t => html`<option value="${t.id}" ${p?.team_id === t.id ? 'selected' : ''}>${t.name}</option>`)}</select></label>
        <label class="field"><span>Valable (jours après la victoire)</span><input name="win_days" type="number" min="1" max="14" value="${p?.win_days ?? 3}"></label>
      </div>
      <label class="check" data-guide="pk-stock"><input type="checkbox" name="limited" ${p?.stock != null ? 'checked' : ''}><span><strong>Nombre limité</strong><br>
        <span class="muted small">Premiers arrivés, premiers servis. À 0, l’avantage s’arrête tout seul et tu es prévenu.</span></span></label>
      <div class="row-2 pk-stock-row" ${p?.stock != null ? '' : 'hidden'}>
        <label class="field"><span>Combien en tout</span><input type="number" name="stock" inputmode="numeric" min="1" max="10000" value="${p?.stock ?? 5}"></label>
        ${p?.stock != null ? html`<p class="muted small pk-stock-now">${p.exhausted_at ? endedLine(p.stock, p.exhausted_at) : stockLine(p.stock_used, p.stock)}</p>` : html`<span></span>`}
      </div>
      <label class="check"><input type="checkbox" name="active" ${p?.active === false ? '' : 'checked'}><span><strong>Proposé aux membres</strong></span></label>
      <p class="hint">« Après une victoire » : seulement les membres de l'équipe qui a gagné (présents au match), une fois par victoire ; ils sont prévenus. Les récompenses des grades se règlent dans Club › Bénévolat.</p>
      <p class="form-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit">Enregistrer</button>
      ${p ? html`<button type="button" class="btn btn-danger-ghost" id="pk-del">Supprimer</button>` : ''}
    </form>`, (sheet, close) => {
    const f = $<HTMLFormElement>(sheet, '#f-pk')!;
    for (const b of $$<HTMLButtonElement>(f, '[data-pic]')) b.addEventListener('click', () => {
      pic = b.dataset.pic!;
      $$(f, '[data-pic]').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
    });
    const freq = $<HTMLSelectElement>(f, '[name=frequency]')!;
    freq.addEventListener('change', () => { $(f, '.pk-win')!.hidden = freq.value !== 'per_win'; });
    const who = $<HTMLSelectElement>(f, '[name=partner]')!;
    who.addEventListener('change', () => { $(f, '.pk-offer-hint')!.hidden = !who.value; });
    const limited = $<HTMLInputElement>(f, '[name=limited]')!;
    limited.addEventListener('change', () => { $(f, '.pk-stock-row')!.hidden = !limited.checked; });
    f.addEventListener('submit', async (e) => {
      e.preventDefault();
      const d = new FormData(f);
      const title = String(d.get('title') ?? '').trim();
      if (!title) { $(f, '.form-msg')!.textContent = 'Donne un nom à l’avantage.'; return; }
      const frequency = String(d.get('frequency')) as PerkFrequency;
      const stock = d.get('limited') === 'on' ? Math.floor(Number(d.get('stock'))) : null;
      if (stock !== null && !(stock >= 1 && stock <= 10000)) { $(f, '.form-msg')!.textContent = 'Combien en tout : au moins 1.'; return; }
      try {
        await savePerk({
          id: p?.id, club_id: club.id, partner_id: String(d.get('partner') ?? '') || null, title,
          details: String(d.get('details') ?? '').trim() || null, picto: pic, frequency,
          team_id: frequency === 'per_win' ? String(d.get('team') ?? '') || null : null,
          win_days: Math.max(1, Math.min(14, Number(d.get('win_days')) || 3)),
          // Plus de niveau ni de points (D-100) : un avantage enregistré ici est pour tous.
          min_level: null, cost_points: 0,
          active: d.get('active') === 'on', valid_until: String(d.get('until') ?? '') || null,
          stock,
        });
        close(); toast('Avantage enregistré'); onDone();
      } catch { $(f, '.form-msg')!.textContent = 'Enregistrement impossible.'; }
    });
    const del = $<HTMLButtonElement>(sheet, '#pk-del');
    if (del && p) confirmTwice(del, 'Confirmer la suppression (son historique part avec)', async () => {
      try { await deletePerk(p.id); close(); toast('Avantage supprimé'); onDone(); }
      catch { toast('Suppression impossible.', 'ko'); }
    });
  });
}
